/**
 * The integration endpoints — slice 3's whole client surface.
 *
 *   fetchSesIntegration  GET    /integrations             what is connected, or null
 *   connectSes           PUT    /integrations/ses         connect or replace, then check
 *   checkSes             POST   /integrations/ses/check   the Test button
 *   disconnectSes        DELETE /integrations/ses         deletes the row and the ciphertext
 *
 * **The secret access key travels in exactly one place: the JSON body of the
 * PUT.** Not a path segment, not a query string, not a header, and not a retry
 * that reconstructs the request from a URL. `apiRequest` builds a query string
 * only from its `query` option, and `connectSes` passes none — deliberately, and
 * that is the whole of the mechanism. A URL is logged by every proxy between
 * here and the API and is kept in browser history; a body is not.
 *
 * **Both ends of every call are checked against the same contract.** The connect
 * body is re-validated with `connectSesRequestSchema` before it leaves the
 * browser, so a mistyped key never becomes a request. Every response is parsed
 * with the shared read shape, which is `.strict()` and has no field a credential
 * could travel in — so an API that started returning one would fail parsing
 * *here* as well as there. The guarantee is checked at both ends rather than
 * remembered at one.
 *
 * **Reading uses the list route, not the fetch-one route.** `GET /integrations`
 * answers `[]` for a workspace that has connected nothing; `GET /integrations/ses`
 * answers 404. "You have not connected SES" is the ordinary starting state of the
 * Integrations screen rather than an error, and turning a 404 into `null` in a
 * catch block means swallowing a 404 that might have been about something else
 * entirely — a revoked session, a mistyped path, a route that moved.
 */
import {
  connectSesRequestSchema,
  integrationKindSchema,
  sesIntegrationSchema,
  okResponseSchema,
  sesCheckOutcomeSchema,
  type ConnectSesRequest,
  type SesIntegration,
  type OkResponse,
} from '@wend/shared';
import { z } from 'zod';

import { apiRequest } from './client';

/* -------------------------------------------------------------------------- */
/* Response envelopes                                                          */
/* -------------------------------------------------------------------------- */

/**
 * The two envelopes below are **compositions of imported shapes**, never
 * re-declarations of one: `sesIntegrationSchema` and `sesCheckOutcomeSchema`
 * come from `@wend/shared` and nothing about an integration is described again
 * here.
 * `@wend/shared` carries every shape but not these two wrappers, and the API
 * composes them the same way in its own `integration-view.ts`.
 *
 * That means they are now written in two packages, which is one more than is
 * comfortable. They belong in `@wend/shared` the moment anything is allowed to
 * move them — see the report for this slice.
 */
/**
 * The list route, parsed **by kind rather than as one shape**.
 *
 * Slice 3 parsed every row with `integrationSchema`, which was right while `ses`
 * was the only kind a row could have. It is wrong now, and it would still be
 * wrong even though `integrationSchema` has since become a union: parsing the
 * whole list as one union means **either** card can be taken down by the other's
 * row — one malformed WhatsApp row and the SES card renders an error about a
 * provider it has nothing to do with. Rows are therefore taken loosely here and
 * narrowed by whoever wants one: `fetchSesIntegration` with
 * `sesIntegrationSchema`, `fetchWhatsAppIntegration` with
 * `metaWhatsAppIntegrationSchema`. Neither can be broken by the other's row, and
 * each is still parsed strictly by the card that renders it.
 */
const integrationRowSchema = z.object({ kind: integrationKindSchema }).passthrough();

const integrationListResponseSchema = z
  .object({ integrations: z.array(integrationRowSchema) })
  .strict();

export type IntegrationRow = z.infer<typeof integrationRowSchema>;

/**
 * Every integration row this workspace has, still unnarrowed.
 *
 * One request, because the two cards on the Integrations screen ask the same
 * question of the same route and there is no reason for that to be two.
 */
export async function fetchIntegrationRows(signal?: AbortSignal): Promise<IntegrationRow[]> {
  const response = await apiRequest({
    path: '/integrations',
    schema: integrationListResponseSchema,
    signal,
  });
  return response.integrations;
}

/**
 * What a connect and a test answer with: the row as it now stands, plus the
 * outcome of the check that just ran.
 *
 * Both halves are needed. `integration` is the persisted state the card renders.
 * `outcome` carries the two things the row cannot: **which transport answered**
 * — a failed check is stored as a bare `lastError` string, so without this a
 * fabricated red from `SES_TRANSPORT=stub` would look exactly like a real one —
 * and the failure **code**, which lets the card head the message with what went
 * wrong instead of parsing the sentence it is already showing.
 */
export const sesCheckResponseSchema = z
  .object({ integration: sesIntegrationSchema, outcome: sesCheckOutcomeSchema })
  .strict();
export type SesCheckResponse = z.infer<typeof sesCheckResponseSchema>;

/* -------------------------------------------------------------------------- */
/* Read                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * The SES integration, or `null` when this workspace has not connected one.
 *
 * There is at most one row per kind — `UNIQUE (tenant_id, kind)` — so this picks
 * rather than filters, and a second SES row would be a database fault, not a
 * list to page through.
 */
export async function fetchSesIntegration(signal?: AbortSignal): Promise<SesIntegration | null> {
  const rows = await fetchIntegrationRows(signal);
  const row = rows.find((integration) => integration.kind === 'ses');
  return row === undefined ? null : sesIntegrationSchema.parse(row);
}

/* -------------------------------------------------------------------------- */
/* Connect                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Connects the tenant's SES account, or replaces the one already connected, and
 * runs a check in the same round trip.
 *
 * `PUT`, because it is idempotent and replaces wholesale: the same body sent
 * twice leaves the same state, and there is no partial update — changing the
 * region means re-entering the key and the secret, because a half-updated
 * credential fails at send time rather than at the form.
 *
 * A check that fails is still a 200. The credentials were stored, the row says
 * `failed`, and `outcome.failure.message` says what to repair — so this rejects
 * only when the *request* failed, never when the credentials did.
 *
 * SECURITY: `request.secretAccessKey` is passed to `fetch` as part of a JSON
 * body and is referenced nowhere else in this module. It is not logged, not put
 * in the URL, not retained, and there is no field on the response it could come
 * back in.
 */
export async function connectSes(request: ConnectSesRequest): Promise<SesCheckResponse> {
  return apiRequest({
    path: '/integrations/ses',
    method: 'PUT',
    body: connectSesRequestSchema.parse(request),
    schema: sesCheckResponseSchema,
  });
}

/* -------------------------------------------------------------------------- */
/* Check                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Re-runs the check against the credentials already stored. The Test button.
 *
 * No body: the credentials are on the server, sealed, and asking the browser to
 * send them again to test them would be the one thing this whole slice is
 * arranged to avoid.
 *
 * This reaches AWS on the tenant's own account, so the API rate-limits it. A 429
 * arrives as an `ApiError` carrying `retryAfterSeconds`, which the screen shows
 * rather than inviting a person to keep pressing.
 */
export async function checkSes(): Promise<SesCheckResponse> {
  return apiRequest({
    path: '/integrations/ses/check',
    method: 'POST',
    schema: sesCheckResponseSchema,
  });
}

/* -------------------------------------------------------------------------- */
/* Disconnect                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Disconnects: the row is deleted and the sealed credential goes with it.
 *
 * Not a status flip. Someone who disconnects has asked Wend to stop holding
 * their key, so what is lost is the key itself — re-connecting means pasting a
 * new one, which the confirmation on the screen says before it happens.
 */
export async function disconnectSes(): Promise<OkResponse> {
  return apiRequest({
    path: '/integrations/ses',
    method: 'DELETE',
    schema: okResponseSchema,
  });
}
